import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Stamp, Gift, MapPin } from "lucide-react";

function StampGrid({ filled, total }) {
  const stamps = Array.from({ length: total }, (_, i) => i < filled);
  return (
    <div className="grid grid-cols-5 gap-2" data-testid="loyalty-stamp-grid">
      {stamps.map((on, i) => (
        <div
          key={`stamp-${i}`}
          className={`aspect-square rounded-full border-2 flex items-center justify-center text-xs font-mono transition-all ${
            on
              ? "bg-[#B31E1E] border-[#B31E1E] text-white shadow-sm"
              : "bg-gray-50 border-dashed border-gray-300 text-gray-300"
          }`}
          aria-label={on ? `Stamp ${i + 1} earned` : `Stamp ${i + 1} empty`}
        >
          {on ? <Stamp size={14} /> : i + 1}
        </div>
      ))}
    </div>
  );
}

export default function LoyaltyStampsCard() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/customer/loyalty")
      .then(({ data }) => setCards(data.cards || []))
      .catch((e) => console.warn("Loyalty cards load failed:", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <Card className="rounded-md border border-border p-6" data-testid="loyalty-cards-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Stamp size={18} className="text-[#B31E1E]" />
        <h3 className="font-display text-2xl">Loyalty stamp cards</h3>
      </div>
      <p className="text-sm text-gray-500 mt-1 max-w-2xl">
        Earn stamps by checking in at participating businesses. Each business has its own card — fill it to unlock a reward.
      </p>

      {cards.length === 0 ? (
        <div className="mt-5 border border-dashed border-border rounded-md p-6 text-center" data-testid="loyalty-cards-empty">
          <MapPin size={24} className="mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">No stamps yet. Find a business with a loyalty program and tap <strong>Check in</strong> on their profile when you visit.</p>
          <Link to="/directory" className="mt-3 inline-block text-sm font-medium text-[#B31E1E] hover:underline">Browse the directory →</Link>
        </div>
      ) : (
        <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="loyalty-cards-list">
          {cards.map((c) => {
            const filled = c.progress;
            const total = c.visits_required;
            const cardFull = filled === 0 && c.visits > 0 && c.rewards_earned > 0;
            return (
              <li
                key={c.business_id}
                className="border border-border rounded-md p-4 bg-white"
                data-testid={`loyalty-card-${c.business_id}`}
              >
                <div className="flex items-start gap-3">
                  {c.business_logo && (
                    <img
                      src={c.business_logo}
                      alt={`${c.business_name} logo`}
                      className="h-10 w-10 rounded-md border border-border object-contain bg-white shrink-0"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <Link to={`/businesses/${c.business_id}`} className="font-display text-lg leading-tight hover:underline truncate block">
                      {c.business_name}
                    </Link>
                    <p className="text-[11px] font-mono text-gray-500 mt-0.5">
                      {filled} / {total} stamps · {c.visits} total visit{c.visits === 1 ? "" : "s"}
                    </p>
                  </div>
                  {c.rewards_earned > 0 && (
                    <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-mono">
                      <Gift size={10} className="mr-1" /> {c.rewards_earned}× earned
                    </Badge>
                  )}
                </div>
                <div className="mt-4">
                  <StampGrid filled={cardFull ? total : filled} total={total} />
                </div>
                {(cardFull || c.rewards_earned > 0) && c.reward_text && (
                  <div className="mt-3 border border-emerald-300 bg-emerald-50 rounded-md p-3" data-testid={`loyalty-reward-${c.business_id}`}>
                    <p className="text-[11px] font-mono uppercase tracking-wider text-emerald-800">Reward ready</p>
                    <p className="text-sm text-emerald-900 mt-1 leading-snug">{c.reward_text}</p>
                    <p className="text-[11px] text-emerald-700 mt-2">Show this on your next visit to redeem.</p>
                  </div>
                )}
                {!c.loyalty_enabled && (
                  <p className="text-[11px] text-gray-500 mt-2">This business has paused their loyalty program.</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
